<template>
  <div class="bladder-report-template" data-testid="bladder-report-template">
    <shared-proforma-fields :report="report" show-tumour-type-detail />

    <section v-if="hasUrothelialCarcinoma" class="report-section" data-testid="bladder-variant-section">
      <h3>{{ $t('urothelial-variant-status') }}</h3>
      <div class="field">
        <label class="label">{{ $t('urothelial-variant-status') }}</label>
        <div class="select is-fullwidth">
          <select
            v-model="report.bladder.variantStatus"
            data-testid="bladder-variant-status"
            @change="handleVariantStatusChange"
          >
            <option v-for="option in variantStatusOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>
      <div
        v-if="report.bladder.variantStatus === 'present'"
        class="variant-details"
        data-testid="bladder-variant-details"
      >
        <div class="field">
          <label class="label">{{ $t('urothelial-variant-subtype-percentage') }}</label>
          <div class="checkbox-grid">
            <label v-for="option in variantSubtypeOptions" :key="option.value" class="checkbox report-checkbox">
              <input
                type="checkbox"
                :value="option.value"
                :checked="report.bladder.variantSubtypes.includes(option.value)"
                @change="toggleCheckbox(report.bladder.variantSubtypes, option.value, $event.target.checked)"
              >
              <span>{{ option.label }}</span>
            </label>
          </div>
        </div>
        <div v-if="report.bladder.variantSubtypes.includes('other')" class="field" data-testid="variant-other-field">
          <label class="label">{{ $t('variant-other-specify') }}</label>
          <textarea class="textarea" rows="2" v-model="report.bladder.variantSubtypeOther" :maxlength="5000"></textarea>
        </div>
        <div class="field">
          <label class="label">{{ $t('variant-percentage-detail') }}</label>
          <textarea class="textarea" rows="2" v-model="report.bladder.variantPercentage" :maxlength="5000"></textarea>
        </div>
      </div>
    </section>

    <section v-if="hasBladderGrading" class="report-section" data-testid="bladder-grading-section">
      <h3>{{ $t('grading-section') }}</h3>
      <div v-if="hasUrothelialCarcinoma" class="field" data-testid="urothelial-grading-fields">
        <label class="label">{{ $t('urothelial-carcinoma-who-isup-grading') }}</label>
        <div class="select is-fullwidth">
          <select v-model="report.bladder.urothelialGrade">
            <option v-for="option in urothelialGradeOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>
      <div v-if="hasUrothelialCarcinoma" class="field" data-testid="urothelial-growth-fields">
        <label class="label">{{ $t('growth-pattern') }}</label>
        <div class="checkbox-grid">
          <label v-for="option in growthPatternOptions" :key="option.value" class="radio report-checkbox">
            <input
              type="radio"
              name="bladder-growth-pattern"
              :value="option.value"
              v-model="report.bladder.growthPattern"
            >
            <span>{{ option.label }}</span>
          </label>
        </div>
      </div>
      <div v-if="hasSccOrAdenocarcinoma" class="field" data-testid="scc-adenocarcinoma-grading-fields">
        <label class="label">{{ $t('scc-adenocarcinoma-grading') }}</label>
        <div class="select is-fullwidth">
          <select v-model="report.bladder.sccAdenocarcinomaGrade">
            <option v-for="option in sccAdenocarcinomaGradeOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>
      <div v-if="hasSccOrAdenocarcinoma && report.bladder.sccAdenocarcinomaGrade === 'other'" class="field">
        <label class="label">{{ $t('other-specify') }}</label>
        <textarea class="textarea" rows="2" v-model="report.bladder.sccAdenocarcinomaGradeOther" :maxlength="5000"></textarea>
      </div>
    </section>

    <section v-if="hasTumourAssessment" class="report-section" data-testid="bladder-depth-section">
      <h3>{{ $t('depth-of-invasion-section') }}</h3>
      <div class="field">
        <label class="label">{{ $t('depth-of-invasion') }}</label>
        <div class="select is-fullwidth">
          <select
            v-model="report.bladder.depthOfInvasion"
            data-testid="depth-of-invasion"
            @change="syncMuscularisFromDepth"
          >
            <option v-for="option in depthOfInvasionOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>
    </section>

    <section v-if="hasTumourAssessment" class="report-section" data-testid="bladder-flat-lesion-section">
      <h3>{{ $t('concomitant-flat-lesion-section') }}</h3>
      <div class="field">
        <label class="label">{{ $t('concomitant-flat-lesion') }}</label>
        <div class="select is-fullwidth">
          <select v-model="report.bladder.concomitantFlatLesion">
            <option v-for="option in concomitantFlatLesionOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>
    </section>

    <section v-if="hasTumourAssessment" class="report-section" data-testid="bladder-muscularis-section">
      <h3>{{ $t('muscularis-propria-section') }}</h3>
      <div class="field">
        <label class="label">{{ $t('muscularis-propria') }}</label>
        <div class="checkbox-grid" data-testid="muscularis-propria">
          <label class="checkbox report-checkbox">
            <input
              type="checkbox"
              value="present"
              :checked="report.bladder.muscularisPropria.presence === 'present'"
              @change="setMuscularisPresence('present', $event.target.checked)"
            >
            <span>{{ $t('option-present') }}</span>
          </label>
          <label class="checkbox report-checkbox">
            <input
              type="checkbox"
              value="absent"
              :checked="report.bladder.muscularisPropria.presence === 'absent'"
              @change="setMuscularisPresence('absent', $event.target.checked)"
            >
            <span>{{ $t('option-absent') }}</span>
          </label>
        </div>
      </div>
      <div v-if="report.bladder.muscularisPropria.presence === 'present'" class="field" data-testid="muscularis-invasion-field">
        <label class="label">{{ $t('muscularis-propria-invasion') }}</label>
        <div class="checkbox-grid">
          <label class="checkbox report-checkbox">
            <input
              type="checkbox"
              value="involved"
              :checked="report.bladder.muscularisPropria.invasion === 'involved'"
              @change="setMuscularisInvasion('involved', $event.target.checked)"
            >
            <span>{{ $t('option-inv') }}</span>
          </label>
          <label class="checkbox report-checkbox">
            <input
              type="checkbox"
              value="not-involved"
              :checked="report.bladder.muscularisPropria.invasion === 'not-involved'"
              @change="setMuscularisInvasion('not-involved', $event.target.checked)"
            >
            <span>{{ $t('option-no-inv') }}</span>
          </label>
        </div>
      </div>
    </section>

    <shared-ending-fields
      :report="report"
      :placeholder="$t('final-interpretation-placeholder')"
      :suggested-final-interpretation="suggestedFinalInterpretation"
      show-suggestion-button
    />
  </div>
</template>

<script>
import SharedEndingFields from './SharedEndingFields';
import SharedProformaFields from './SharedProformaFields';
import { optionPairs, translateOptions } from './reportTemplateOptions';
import { buildBladderFinalInterpretation } from './reportTemplateText';

export default {
  name: 'BladderReportTemplate',
  components: {
    SharedEndingFields,
    SharedProformaFields
  },
  props: {
    report: {
      type: Object,
      required: true
    }
  },
  computed: {
    variantStatusOptions() {
      return translateOptions(this.$t.bind(this), optionPairs.variantStatus);
    },
    variantSubtypeOptions() {
      return translateOptions(this.$t.bind(this), optionPairs.variantSubtype);
    },
    urothelialGradeOptions() {
      return translateOptions(this.$t.bind(this), optionPairs.urothelialGrade);
    },
    growthPatternOptions() {
      return translateOptions(this.$t.bind(this), optionPairs.growthPattern);
    },
    sccAdenocarcinomaGradeOptions() {
      return translateOptions(this.$t.bind(this), optionPairs.sccAdenocarcinomaGrade);
    },
    depthOfInvasionOptions() {
      return translateOptions(this.$t.bind(this), optionPairs.depthOfInvasion);
    },
    concomitantFlatLesionOptions() {
      return translateOptions(this.$t.bind(this), optionPairs.concomitantFlatLesion);
    },
    selectedTumourType() {
      return this.report.microscopic.tumourType || '';
    },
    hasUrothelialCarcinoma() {
      return this.selectedTumourType === 'urothelial-carcinoma';
    },
    hasSccOrAdenocarcinoma() {
      return [
        'squamous-cell-carcinoma',
        'adenocarcinoma',
        'clear-cell-carcinoma',
        'endometrioid-carcinoma'
      ].includes(this.selectedTumourType);
    },
    hasBladderGrading() {
      return this.hasUrothelialCarcinoma || this.hasSccOrAdenocarcinoma;
    },
    hasTumourAssessment() {
      return Boolean(this.selectedTumourType && this.selectedTumourType !== 'benign-condition');
    },
    suggestedFinalInterpretation() {
      return buildBladderFinalInterpretation(this.report, this.$t.bind(this));
    }
  },
  watch: {
    'report.microscopic.tumourType': {
      handler() {
        this.resetHiddenBladderFields();
      }
    }
  },
  methods: {
    resetHiddenBladderFields() {
      if (!this.hasUrothelialCarcinoma) {
        this.report.bladder.variantStatus = '';
        this.report.bladder.variantSubtypes = [];
        this.report.bladder.variantSubtypeOther = '';
        this.report.bladder.variantPercentage = '';
        this.report.bladder.urothelialGrade = '';
        this.report.bladder.growthPattern = '';
      }
      if (!this.hasSccOrAdenocarcinoma) {
        this.report.bladder.sccAdenocarcinomaGrade = '';
        this.report.bladder.sccAdenocarcinomaGradeOther = '';
      }
      if (this.selectedTumourType !== 'benign-condition') {
        this.report.microscopic.tumourTypeDetail = '';
      }
      if (this.selectedTumourType !== 'other') {
        this.report.microscopic.tumourTypeOther = '';
      }
      if (!this.hasTumourAssessment) {
        this.report.bladder.depthOfInvasion = '';
        this.report.bladder.concomitantFlatLesion = '';
        this.report.bladder.muscularisPropria.presence = '';
        this.report.bladder.muscularisPropria.invasion = '';
      }
    },
    setMuscularisPresence(value, checked) {
      this.report.bladder.muscularisPropria.presence = checked ? value : '';
      if (value === 'absent' || !checked) {
        this.report.bladder.muscularisPropria.invasion = '';
      }
      if ((value === 'absent' || !checked) && this.report.bladder.depthOfInvasion === 't2-muscularis-propria-invasion') {
        this.report.bladder.depthOfInvasion = '';
      }
    },
    setMuscularisInvasion(value, checked) {
      this.report.bladder.muscularisPropria.invasion = checked ? value : '';
      if (checked && value === 'involved') {
        this.report.bladder.depthOfInvasion = 't2-muscularis-propria-invasion';
      } else if ((!checked || value === 'not-involved') && this.report.bladder.depthOfInvasion === 't2-muscularis-propria-invasion') {
        this.report.bladder.depthOfInvasion = '';
      }
    },
    syncMuscularisFromDepth() {
      if (this.report.bladder.depthOfInvasion === 't2-muscularis-propria-invasion') {
        this.report.bladder.muscularisPropria.presence = 'present';
        this.report.bladder.muscularisPropria.invasion = 'involved';
      }
    },
    handleVariantStatusChange() {
      if (this.report.bladder.variantStatus !== 'present') {
        this.report.bladder.variantSubtypes = [];
        this.report.bladder.variantSubtypeOther = '';
        this.report.bladder.variantPercentage = '';
      }
    },
    toggleCheckbox(target, value, checked) {
      const index = target.indexOf(value);
      if (checked && index === -1) {
        target.push(value);
      } else if (!checked && index !== -1) {
        target.splice(index, 1);
      }
      if (value === 'other' && !checked) {
        this.report.bladder.variantSubtypeOther = '';
      }
    }
  }
};
</script>
